/**
 * React hook for browser notifications
 * Provides easy access to browser notification functionality
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  browserNotifications,
  type NotificationPermissionStatus,
  type BrowserNotificationOptions,
} from '@services/browserNotifications';

export interface UseBrowserNotificationsReturn {
  isSupported: boolean;
  isEnabled: boolean;
  permissionStatus: NotificationPermissionStatus;
  requestPermission: () => Promise<NotificationPermissionStatus>;
  show: (options: BrowserNotificationOptions) => Promise<Notification | null>;
  showTaskNotification: (
    title: string,
    message: string,
    taskId: string,
    priority: 'low' | 'normal' | 'high' | 'urgent'
  ) => Promise<Notification | null>;
  showCommentNotification: (
    title: string,
    message: string,
    taskId: string,
    commentId: string
  ) => Promise<Notification | null>;
  showMeetingNotification: (
    title: string,
    message: string,
    meetingId: string
  ) => Promise<Notification | null>;
  showDraftTasksNotification: (
    title: string,
    message: string,
    sourceId: string,
    sourceType: 'email' | 'meeting'
  ) => Promise<Notification | null>;
  showTestNotification: () => Promise<Notification | null>;
}

/**
 * Hook to manage browser notifications
 */
export function useBrowserNotifications(): UseBrowserNotificationsReturn {
  const navigate = useNavigate();
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermissionStatus>(
    browserNotifications.getPermissionStatus()
  );

  // Update permission status on mount and when it changes.
  // Prefer the Permissions API's `change` event (event-driven, no polling);
  // fall back to a slow poll only where it's unavailable (e.g. Safari), and
  // pause polling while the tab is hidden.
  useEffect(() => {
    const updatePermissionStatus = () => {
      setPermissionStatus(browserNotifications.getPermissionStatus());
    };

    updatePermissionStatus();

    let cancelled = false;
    let permissionStatusRef: PermissionStatus | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (interval !== null) return;
      // Slow poll; only runs while the tab is visible.
      interval = setInterval(() => {
        if (!document.hidden) {
          updatePermissionStatus();
        }
      }, 10000);
    };

    const stopPolling = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };

    if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((status) => {
          if (cancelled) return;
          permissionStatusRef = status;
          status.addEventListener('change', updatePermissionStatus);
          updatePermissionStatus();
        })
        .catch(() => {
          // Permissions API present but query rejected — fall back to polling.
          if (!cancelled) startPolling();
        });
    } else {
      // No Permissions API (e.g. Safari) — poll slowly instead.
      startPolling();
    }

    return () => {
      cancelled = true;
      stopPolling();
      if (permissionStatusRef) {
        permissionStatusRef.removeEventListener('change', updatePermissionStatus);
      }
    };
  }, []);

  const requestPermission = useCallback(async (): Promise<NotificationPermissionStatus> => {
    const status = await browserNotifications.requestPermission();
    setPermissionStatus(status);
    return status;
  }, []);

  const show = useCallback(
    async (options: BrowserNotificationOptions): Promise<Notification | null> => {
      return browserNotifications.show(options);
    },
    []
  );

  const showTaskNotification = useCallback(
    async (
      title: string,
      message: string,
      taskId: string,
      priority: 'low' | 'normal' | 'high' | 'urgent'
    ): Promise<Notification | null> => {
      return browserNotifications.showTaskNotification(
        title,
        message,
        taskId,
        priority,
        (url) => navigate(url)
      );
    },
    [navigate]
  );

  const showCommentNotification = useCallback(
    async (
      title: string,
      message: string,
      taskId: string,
      commentId: string
    ): Promise<Notification | null> => {
      return browserNotifications.showCommentNotification(
        title,
        message,
        taskId,
        commentId,
        (url) => navigate(url)
      );
    },
    [navigate]
  );

  const showMeetingNotification = useCallback(
    async (title: string, message: string, meetingId: string): Promise<Notification | null> => {
      return browserNotifications.showMeetingNotification(
        title,
        message,
        meetingId,
        (url) => navigate(url)
      );
    },
    [navigate]
  );

  const showDraftTasksNotification = useCallback(
    async (
      title: string,
      message: string,
      sourceId: string,
      sourceType: 'email' | 'meeting'
    ): Promise<Notification | null> => {
      return browserNotifications.showDraftTasksNotification(
        title,
        message,
        sourceId,
        sourceType,
        (url) => navigate(url)
      );
    },
    [navigate]
  );

  const showTestNotification = useCallback(async (): Promise<Notification | null> => {
    return browserNotifications.showTestNotification();
  }, []);

  return {
    isSupported: browserNotifications.isSupported(),
    isEnabled: permissionStatus === 'granted',
    permissionStatus,
    requestPermission,
    show,
    showTaskNotification,
    showCommentNotification,
    showMeetingNotification,
    showDraftTasksNotification,
    showTestNotification,
  };
}
